import React, { useState, useRef, useEffect, useCallback } from 'react';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import SectionSidebar from '../components/SectionSidebar';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  Chip,
} from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import MapIcon from '@mui/icons-material/Map';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NearMeIcon from '@mui/icons-material/NearMe';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const GOLD = '#d4af37';

const quickLinks = [
  { icon: InfoIcon, label: 'Contact Info', desc: 'Phone, email & address details', gradient: ['#3b82f6', '#2563eb'] as [string, string], to: '/contact/info' },
  { icon: MapIcon, label: 'Map & Directions', desc: 'How to reach the temple', gradient: ['#22c55e', '#16a34a'] as [string, string], to: '/contact/map' },
  { icon: FeedbackIcon, label: 'Feedback Form', desc: 'Share your experience', gradient: ['#ec4899', '#db2777'] as [string, string], to: '/contact/feedback' },
];

const faqs = [
  { q: 'How can I book a pooja online?', a: 'Visit the Poojas section on our website, select your preferred pooja, choose a date, and complete the payment through our secure online booking system.', icon: QuestionAnswerIcon },
  { q: 'Is photography allowed?', a: 'Photography inside sanctum areas is restricted; follow signage and staff instructions.', icon: QuestionAnswerIcon },
  { q: 'Where can I park?', a: 'Public parking is available near the east gate; use marked parking areas to the south.', icon: QuestionAnswerIcon },
];

export default function Contact() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const TURNSTILE_SITE_KEY = '0x4AAAAAACpmoaG-ENnK6t7-';

  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !window.turnstile) return;
    if (widgetIdRef.current !== null) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(null),
      'error-callback': () => setTurnstileToken(null),
      theme: 'light',
    });
  }, [TURNSTILE_SITE_KEY]);

  useEffect(() => {
    if (window.turnstile) {
      renderTurnstile();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          renderTurnstile();
        }
      }, 300);
      return () => clearInterval(interval);
    }
    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderTurnstile]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!turnstileToken) {
      alert('Please complete the verification before sending your message.');
      return;
    }
    setSending(true);
    try {
      await addDoc(collection(db, 'contact_messages'), {
        ...form,
        formType: 'contact',
        read: false,
        createdAt: serverTimestamp(),
        ...(user ? { userId: user.uid, userEmail: user.email } : {}),
      });
      alert('Message sent successfully! Temple office will contact you shortly.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err: any) {
      alert('Error sending message: ' + (err?.message || 'Please try again later.'));
    } finally {
      setSending(false);
      setTurnstileToken(null);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      fontSize: '0.85rem',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
      '&:hover fieldset': { borderColor: '#06b6d4' },
      '&.Mui-focused fieldset': { borderColor: '#06b6d4', borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#06b6d4' },
  };

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO
        title="Contact & Visit — Kottayil Temple | സംപർക്കം"
        description="Contact Kottayil Temple (Kotta-Kovilakam Kshethram) at Muchukunnu, Kozhikode. Directions, visiting hours, phone numbers, and how to reach."
        path="/contact"
        keywords="Kottayil temple contact, Kottayil temple Muchukunnu contact, Muchukunnu temple address, Kottayil Shiva temple address, Kottayil temple directions, visiting hours, temple phone number, how to reach Kottayil temple Muchukunnu, Kozhikode temple contact, Koyilandy temple, കൊട്ടയിൽ ക്ഷേത്രം വിലാസം, കൊട്ടയിൽ ക്ഷേത്രം മുച്ചുകുന്ന്, മുച്ചുകുന്ന് ക്ഷേത്രം"
      />
      <PageBanner title="Contact & Visit" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #164e63 0%, #0e7490 40%, #06b6d4 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <ContactMailIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Contact & Visit Us
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Send a message or plan your visit to the temple
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  We are always happy to hear from devotees. Reach out to us for pooja bookings, donations, feedback, or any queries about the temple.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<SupportAgentIcon sx={{ fontSize: '16px !important', color: '#06b6d4 !important' }} />} label="Helpdesk" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#164e63', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<NearMeIcon sx={{ fontSize: '16px !important', color: '#67e8f9 !important' }} />} label="Visit Planning" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* QUICK LINKS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {quickLinks.map((lnk) => {
                const LIcon = lnk.icon;
                const [gS, gE] = lnk.gradient;
                return (
                  <Grid item xs={12} sm={4} key={lnk.label}>
                    <Box
                      onClick={() => navigate(lnk.to)}
                      sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                        cursor: 'pointer',
                        transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(0,0,0,0.1), 0 0 0 1px ${gS}15` },
                      }}
                    >
                      <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                          <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <LIcon sx={{ fontSize: 22, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>{lnk.label}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ px: 2.5, py: 1.8 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.6 }}>{lnk.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* CONTACT FORM + SIDE INFO */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} md={7}>
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', px: 3, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                        <SendIcon sx={{ fontSize: 18, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Send Us a Message</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 1.8 }}>
                      <TextField label="Full Name" name="name" value={form.name} onChange={handleChange} required size="small" sx={inputSx} />
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}><TextField label="Email" name="email" value={form.email} onChange={handleChange} required size="small" fullWidth sx={inputSx} /></Grid>
                        <Grid item xs={12} sm={6}><TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} size="small" fullWidth sx={inputSx} /></Grid>
                      </Grid>
                      <TextField label="Subject" name="subject" value={form.subject} onChange={handleChange} size="small" sx={inputSx} />
                      <TextField label="Message" name="message" value={form.message} onChange={handleChange} multiline rows={4} required size="small" sx={inputSx} />
                      <Box ref={turnstileRef} sx={{ display: 'flex', justifyContent: 'center' }} />
                      <Button type="submit" variant="contained" disabled={!turnstileToken || sending} startIcon={<SendIcon sx={{ fontSize: 16 }} />} sx={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: '#fff', fontWeight: 700, borderRadius: '14px', py: 1.2, fontSize: '0.82rem', textTransform: 'none', boxShadow: '0 6px 20px rgba(6,182,212,0.3)', '&:hover': { background: 'linear-gradient(135deg, #0891b2, #0e7490)', transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(6,182,212,0.4)' }, '&.Mui-disabled': { background: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)', boxShadow: 'none' } }}>
                        {sending ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                {/* Office Hours */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 2.5 }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', px: 2.5, py: 1.8, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -10, right: -10, width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AccessTimeIcon sx={{ fontSize: 17, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>Office Hours</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#8b5cf6', mb: 0.5 }}>Kottayil Temple</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Temple Open</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>6:00 AM – 11:00 AM</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Office Desk</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>6:00 AM – 11:00 AM</Typography>
                    </Box>
                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.06)', my: 1 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#8b5cf6', mb: 0.5 }}>Kovilakam Temple</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Temple Open</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 AM – 08:30 AM</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 PM – 07:30 PM</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Office Desk</Typography>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 AM – 08:30 AM</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>5:30 PM – 07:30 PM</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {/* Quick Contact */}
                <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}>
                  <Box sx={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', px: 2.5, py: 1.8, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -10, right: -10, width: 50, height: 50, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PhoneIcon sx={{ fontSize: 17, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>Quick Contact</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {[{ label: 'Temple Office', val: '0496 2690751' }, { label: 'Booking Desk', val: '0496 2690751' }].map((c) => (
                      <Box key={c.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.6 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{c.label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#22c55e' }}>{c.val}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* MAPS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {[
                { name: 'Kottayil Temple', src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.8!2d75.51488!3d11.79765!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba688f70ba4cc09%3A0xa1db14313acaad96!2sMuchukunnu%20Kottayil%20Sree%20Shiva%20Temple!5e0!3m2!1sen!2sin!4v1234567890', gradient: ['#f59e0b', '#d97706'] },
                { name: 'Kovilakam Temple', src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.8!2d75.67279!3d11.479135!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba689a95971377f%3A0xe8693ab59eea155c!2sKovilakam%20Temple!5e0!3m2!1sen!2sin!4v1234567890', gradient: ['#ef4444', '#dc2626'] },
              ].map((m) => (
                <Grid item xs={12} md={6} key={m.name}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%' }}>
                    <Box sx={{ background: `linear-gradient(135deg, ${m.gradient[0]}, ${m.gradient[1]})`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#fff' }}>{m.name}</Typography>
                    </Box>
                    <iframe
                      title={`${m.name} Location`}
                      src={m.src}
                      width="100%"
                      height="280"
                      style={{ border: 0, display: 'block' }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* FAQ FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <HelpOutlineIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Visitor FAQ</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Quick answers to common questions</Typography>
                  </Box>
                </Box>
                {faqs.map((f, i) => (
                  <Box key={i} sx={{ mb: i < faqs.length - 1 ? 2 : 0, py: 1, px: 1.5, borderRadius: '12px', transition: 'all 250ms', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: `${GOLD}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <QuestionAnswerIcon sx={{ fontSize: 13, color: GOLD }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>{f.q}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, ml: 4.5 }}>{f.a}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="contact" activePath="/contact" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
